import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  useTheme,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormikControl from "src/components/formik/FormikControl";

interface LeaveFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: any) => void;
  initialValues?: any;
}

export const LeaveFormModal: React.FC<LeaveFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialValues,
}) => {
  const theme = useTheme();

  const defaultValues = {
    name: "",
    quota: "",
    category: "Paid",
    carryForward: false,
    isActive: true,
    effectiveFrom: new Date().toISOString().split("T")[0],
  };

  const formInitialValues = { ...defaultValues, ...initialValues };

  const validationSchema = Yup.object({
    name: Yup.string().required("Leave name is required"),
    quota: Yup.number()
      .typeError("Quota must be a number")
      .required("Quota is required")
      .min(0, "Quota cannot be negative"),
    category: Yup.string()
      .oneOf(["Paid", "Unpaid"], "Invalid category")
      .required("Category is required"),
    effectiveFrom: Yup.date()
      .required("Effective date is required")
      .typeError("Invalid date format"),
  });

  const handleSubmit = async (values: any) => {
    const formattedValues = {
      name: values.name,
      quota: Number(values.quota),
      category: values.category,
      carryForward:
        values.carryForward === true ||
        values.carryForward === "true" ||
        values.carryForward === "Yes",
      isActive: values.isActive ?? true,
      effectiveFrom: new Date(values.effectiveFrom).toISOString().split("T")[0],
    };

    if (initialValues?.id) {
      formattedValues["id"] = initialValues.id;
    }

    // ✅ Wait for async submit to finish
    await onSubmit(formattedValues);

    // ✅ Only close when successful
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "70vh", // 🔹 Increased height
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* 🔹 Colored Header */}
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          p: 2.5,
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {initialValues ? "Edit Leave Type" : "Create Leave Type"}
        </Typography>
      </Box>

      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ getFieldProps }) => (
          <Form style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <DialogContent sx={{ flex: 1, overflowY: "auto", mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormikControl
                    control="InputField"
                    label="Leave Name"
                    name="name"
                    required
                    {...getFieldProps("name")}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormikControl
                    control="InputField"
                    label="Quota (Days)"
                    name="quota"
                    required
                    {...getFieldProps("quota")}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormikControl
                    control="SelectField"
                    label="Category"
                    name="category"
                    options={[
                      { label: "Paid", value: "Paid" },
                      { label: "Unpaid", value: "Unpaid" },
                    ]}
                    {...getFieldProps("category")}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormikControl
                    control="SelectField"
                    label="Carry Forward"
                    name="carryForward"
                    options={[
                      { label: "Yes", value: true },
                      { label: "No", value: false },
                    ]}
                    {...getFieldProps("carryForward")}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormikControl
                    control="CalendarTime"
                    label="Effective From"
                    dateOrTimeOnly="date"
                    name="effectiveFrom"
                    {...getFieldProps("effectiveFrom")}
                  />
                </Grid>

                {initialValues && (
                  <Grid item xs={6}>
                    <FormikControl
                      control="SelectField"
                      label="Active Status"
                      name="isActive"
                      options={[
                        { label: "Active", value: true },
                        { label: "Inactive", value: false },
                      ]}
                      {...getFieldProps("isActive")}
                    />
                  </Grid>
                )}
              </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={onClose} color="secondary">
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Save
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
